/**
 * LandingSettings — MASTER_ADMIN-only CMS for the public landing page and
 * the login page. Lets the admin override text strings in EN / ES / FR / HT
 * with a draft → publish workflow. Images, colours and layout controls are
 * scoped for Phase 2/3.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Save,
  Eye,
  Send,
  RotateCcw,
  RefreshCcw,
  Undo2,
  Loader2,
  Check,
  AlertCircle,
  Languages,
  FileText,
  Home,
  Layers,
  Boxes,
  Package,
  Handshake,
  Phone,
  ChevronDown,
  Palette,
  LogIn,
  Plus,
  Trash2,
  Scale,
  GripVertical,
  Image as ImageIcon,
  SlidersHorizontal,
  Search,
  Share2,
  History,
  Smartphone,
  Tablet,
  Monitor,
  ExternalLink,
  BarChart3,
  Type,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { LANDING_SECTIONS, CMS_LANGUAGES } from "@/lib/landingSchema";
import FlagIcon from "@/components/ui/FlagIcon";
import ImageUploader from "@/components/ImageUploader";
import { useBranding, whatsappHref } from "@/context/BrandingContext";

const SECTION_ICONS = {
  global: Palette,
  visuals: ImageIcon,
  home: Home,
  navigation: Layers,
  about: FileText,
  services: Boxes,
  products: Package,
  partners: Handshake,
  contact: Phone,
  footer: FileText,
  login: LogIn,
  legal: Scale,
  landing_stats: BarChart3,
  design: SlidersHorizontal,
  seo: Search,
  social: Share2,
  history: History,
};

/**
 * Deep-clone the CMS draft into the shape the editor manipulates:
 * translations = { en: { key: value }, es: {...}, fr: {...}, ht: {...} }
 * branding     = { brand_name, brand_tagline, default_language }
 * toggles      = { hero: bool, ... }
 */
function normalise(doc) {
  const empty = {
    branding: {},
    translations: { en: {}, es: {}, fr: {}, ht: {} },
    toggles: {},
    images: {},
    partners: [],
    legal_pages: { privacy: {}, terms: {} },
    design: {},
    seo: { per_language: { en: {}, es: {}, fr: {}, ht: {} } },
    social: {},
    app_stores: {},
  };
  if (!doc) return empty;
  return {
    branding: { ...(doc.branding || {}) },
    translations: {
      en: { ...(doc.translations?.en || {}) },
      es: { ...(doc.translations?.es || {}) },
      fr: { ...(doc.translations?.fr || {}) },
      ht: { ...(doc.translations?.ht || {}) },
    },
    toggles: { ...(doc.toggles || {}) },
    images: { ...(doc.images || {}) },
    partners: [...(doc.partners || [])],
    legal_pages: {
      privacy: { ...(doc.legal_pages?.privacy || {}) },
      terms: { ...(doc.legal_pages?.terms || {}) },
    },
    design: { ...(doc.design || {}) },
    seo: {
      canonical_url: doc.seo?.canonical_url || "",
      allow_indexing: doc.seo?.allow_indexing !== false,
      og_image_slot: doc.seo?.og_image_slot || "hero_bg",
      per_language: {
        en: { ...(doc.seo?.per_language?.en || {}) },
        es: { ...(doc.seo?.per_language?.es || {}) },
        fr: { ...(doc.seo?.per_language?.fr || {}) },
        ht: { ...(doc.seo?.per_language?.ht || {}) },
      },
    },
    social: { ...(doc.social || {}) },
    app_stores: { ...(doc.app_stores || {}) },
    updated_at: doc.updated_at || null,
    updated_by: doc.updated_by || null,
    published_version: doc.published_version || null,
  };
}

// Compares two CMS docs across every editable block. Returns true if any
// of them differ. Used to derive both "unsaved local edits" and
// "draft has unpublished changes" states.
function docsDiffer(a, b) {
  if (!a || !b) return false;
  const keys = [
    "translations", "branding", "toggles", "images", "partners",
    "legal_pages", "design", "seo", "social", "app_stores",
  ];
  return keys.some((k) => JSON.stringify(a[k]) !== JSON.stringify(b[k]));
}

export default function LandingSettings() {
  const [draft, setDraft] = useState(null);
  // savedDraft mirrors the last state confirmed by the server (after a
  // successful GET, PUT/save, or publish/rollback). "dirty" is derived
  // from draft vs savedDraft — so pressing Save flips the badge to clean
  // immediately, independent of the published version.
  const [savedDraft, setSavedDraft] = useState(null);
  const [published, setPublished] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [activeSection, setActiveSection] = useState(LANDING_SECTIONS[0].id);
  const [activeLang, setActiveLang] = useState("en");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTarget, setPreviewTarget] = useState("home");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [d, p] = await Promise.all([
          api.get("/landing-settings/admin/draft"),
          api.get("/landing-settings/admin/published"),
        ]);
        if (cancelled) return;
        const normDraft = normalise(d.data);
        setDraft(normDraft);
        setSavedDraft(normDraft);
        setPublished(normalise(p.data));
      } catch (err) {
        toast.error(err.response?.data?.detail || "Failed to load Landing Settings");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Unsaved local edits (local draft differs from the server-confirmed draft).
  const dirty = useMemo(
    () => docsDiffer(draft, savedDraft),
    [draft, savedDraft],
  );
  // Saved but not yet published (server draft differs from published).
  const draftAhead = useMemo(
    () => docsDiffer(savedDraft, published),
    [savedDraft, published],
  );

  const section = LANDING_SECTIONS.find((s) => s.id === activeSection) || LANDING_SECTIONS[0];

  const updateTranslation = (lang, key, value) => {
    setDraft((prev) => ({
      ...prev,
      translations: {
        ...prev.translations,
        [lang]: {
          ...(prev.translations[lang] || {}),
          [key]: value,
        },
      },
    }));
  };

  const updateBranding = (key, value) => {
    setDraft((prev) => ({ ...prev, branding: { ...prev.branding, [key]: value } }));
  };

  const updateToggle = (key, value) => {
    setDraft((prev) => ({ ...prev, toggles: { ...prev.toggles, [key]: value } }));
  };

  const saveDraft = async () => {
    setBusy(true);
    try {
      const payload = {
        translations: draft.translations,
        branding: draft.branding,
        toggles: draft.toggles,
        design: draft.design,
        seo: draft.seo,
        social: draft.social,
        app_stores: draft.app_stores,
      };
      const { data } = await api.put("/landing-settings/admin/draft", payload);
      const norm = normalise(data);
      setDraft(norm);
      setSavedDraft(norm);
      toast.success("Draft saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save draft");
    } finally {
      setBusy(false);
    }
  };

  const publish = async () => {
    if (!confirm("Publish the current draft to the public landing page and login page?")) return;
    setBusy(true);
    try {
      await api.put("/landing-settings/admin/draft", {
        translations: draft.translations,
        branding: draft.branding,
        toggles: draft.toggles,
        design: draft.design,
        seo: draft.seo,
        social: draft.social,
        app_stores: draft.app_stores,
      });
      const { data } = await api.post("/landing-settings/admin/publish");
      const normPub = normalise(data.published);
      setPublished(normPub);
      // After a publish, the saved draft equals published — so both dirty
      // and draftAhead flip to false and the badge shows "In sync".
      setSavedDraft(normPub);
      setDraft(normPub);
      toast.success("Published to production");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to publish");
    } finally {
      setBusy(false);
    }
  };

  const revertDraft = async () => {
    if (!confirm("Discard your unsaved draft and reload from the last published version?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/landing-settings/admin/revert-draft");
      const norm = normalise(data);
      setDraft(norm);
      setSavedDraft(norm);
      toast.success("Draft restored from published version");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to revert");
    } finally {
      setBusy(false);
    }
  };

  const resetDefaults = async () => {
    if (!confirm("Reset the draft to factory defaults? All overrides will be cleared — this cannot be undone without republishing.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/landing-settings/admin/reset-defaults");
      const norm = normalise(data);
      setDraft(norm);
      setSavedDraft(norm);
      toast.success("Draft reset to defaults");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reset");
    } finally {
      setBusy(false);
    }
  };

  const preview = (kind) => {
    const path = kind === "login" ? "/login" : "/";
    window.open(`${path}?preview_landing=1`, "_blank");
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center text-slate-500">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading Landing Settings…
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px] mx-auto space-y-6" data-testid="landing-settings-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Branding Settings
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage the content of the public landing page (<code>/</code>) and the login page.
            Changes go through a <b>Draft → Publish</b> workflow — nothing appears publicly until you press Publish.
          </p>
        </div>
        <StatusBadge dirty={dirty} draftAhead={draftAhead} updatedAt={draft?.updated_at} version={published?.published_version} />
      </div>

      {/* Action bar */}
      <div className="flex flex-wrap gap-2 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
        <Btn onClick={saveDraft} busy={busy} icon={Save} testid="landing-save">Save draft</Btn>
        <Btn onClick={() => { setPreviewTarget("home"); setPreviewOpen(true); }} icon={Eye} variant="outline" testid="landing-preview-embed">Preview (embed)</Btn>
        <Btn onClick={() => preview("home")} icon={ExternalLink} variant="outline" testid="landing-preview-home">Landing ↗</Btn>
        <Btn onClick={() => preview("login")} icon={ExternalLink} variant="outline" testid="landing-preview-login">Login ↗</Btn>
        <Btn onClick={publish} busy={busy} icon={Send} variant="primary" testid="landing-publish">Publish</Btn>
        <span className="flex-1" />
        <Btn onClick={revertDraft} busy={busy} icon={Undo2} variant="ghost" testid="landing-revert">Discard changes</Btn>
        <Btn onClick={resetDefaults} busy={busy} icon={RotateCcw} variant="ghost" testid="landing-reset-defaults">Reset to defaults</Btn>
      </div>

      <div className="grid gap-6 md:grid-cols-[240px_1fr]">
        {/* Sidebar sections */}
        <nav className="rounded-2xl border border-slate-200 bg-white p-2 self-start sticky top-4" data-testid="landing-sections">
          <ul className="space-y-1">
            {LANDING_SECTIONS.map((sec) => {
              const Icon = SECTION_ICONS[sec.id] || FileText;
              const active = sec.id === activeSection;
              return (
                <li key={sec.id}>
                  <button
                    type="button"
                    onClick={() => setActiveSection(sec.id)}
                    className={`flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm text-left transition-colors ${
                      active
                        ? "bg-[#0F62FE] text-white font-semibold"
                        : "text-slate-700 hover:bg-slate-100"
                    }`}
                    data-testid={`landing-section-${sec.id}`}
                  >
                    <Icon className="w-4 h-4 shrink-0" />
                    <span className="flex-1 truncate">{sec.title}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Section editor */}
        <div className="min-w-0" data-testid="landing-editor">
          <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
              <div>
                <h2 className="text-lg font-bold text-slate-900">{section.title}</h2>
                {section.description && (
                  <p className="text-sm text-slate-500 mt-1">{section.description}</p>
                )}
              </div>
              {!section.branding && !section.partners && !section.legal
                && !section.design && !section.social && !section.history
                && !section.visuals && !section.landing_stats && (
                <LanguageTabs value={activeLang} onChange={setActiveLang} />
              )}
              {(section.legal || section.seo) && (
                <LanguageTabs value={activeLang} onChange={setActiveLang} />
              )}
            </div>

            {section.branding ? (
              <GlobalBrandingEditor draft={draft} update={updateBranding} />
            ) : section.visuals ? (
              <LogoVisualsTab />
            ) : section.landing_stats ? (
              <PublicLandingTab />
            ) : section.partners ? (
              <PartnersEditor
                partners={draft.partners}
                onChange={(nextPartners) => {
                  // Partners are persisted server-side per-op, so sync both
                  // local draft and savedDraft to keep the dirty flag honest.
                  setDraft((d) => ({ ...d, partners: nextPartners }));
                  setSavedDraft((d) => (d ? { ...d, partners: nextPartners } : d));
                }}
                translationFields={
                  <TranslationFields
                    fields={section.fields}
                    lang={activeLang}
                    values={draft.translations[activeLang] || {}}
                    onChange={(key, val) => updateTranslation(activeLang, key, val)}
                    sectionId={section.id}
                    toggles={draft.toggles}
                    onToggle={updateToggle}
                  />
                }
                activeLangTabs={<LanguageTabs value={activeLang} onChange={setActiveLang} />}
              />
            ) : section.legal ? (
              <LegalEditor
                lang={activeLang}
                legal={draft.legal_pages}
                onChange={(page, entry) => {
                  // Legal pages are saved to the backend inside the editor,
                  // so mirror the change into savedDraft too.
                  const patcher = (d) => (d ? {
                    ...d,
                    legal_pages: {
                      ...d.legal_pages,
                      [page]: { ...(d.legal_pages[page] || {}), [activeLang]: entry },
                    },
                  } : d);
                  setDraft(patcher);
                  setSavedDraft(patcher);
                }}
              />
            ) : section.design ? (
              <DesignEditor
                design={draft.design}
                toggles={draft.toggles}
                onChangeDesign={(patch) => setDraft((d) => ({ ...d, design: { ...(d.design || {}), ...patch } }))}
                onChangeToggle={(k, v) => setDraft((d) => ({ ...d, toggles: { ...(d.toggles || {}), [k]: v } }))}
              />
            ) : section.seo ? (
              <SeoEditor
                lang={activeLang}
                seo={draft.seo}
                onChange={(patch) => setDraft((d) => ({ ...d, seo: { ...(d.seo || {}), ...patch } }))}
                onChangeLang={(langCode, patch) => setDraft((d) => ({
                  ...d,
                  seo: {
                    ...(d.seo || {}),
                    per_language: {
                      ...(d.seo?.per_language || {}),
                      [langCode]: { ...(d.seo?.per_language?.[langCode] || {}), ...patch },
                    },
                  },
                }))}
              />
            ) : section.social ? (
              <SocialAppStoresEditor
                social={draft.social}
                appStores={draft.app_stores}
                onChangeSocial={(key, patch) => setDraft((d) => ({
                  ...d,
                  social: { ...(d.social || {}), [key]: { ...(d.social?.[key] || {}), ...patch } },
                }))}
                onChangeStore={(key, patch) => setDraft((d) => ({
                  ...d,
                  app_stores: { ...(d.app_stores || {}), [key]: { ...(d.app_stores?.[key] || {}), ...patch } },
                }))}
              />
            ) : section.history ? (
              <HistoryBrowser
                onRestored={(restored) => {
                  const norm = normalise(restored);
                  setDraft(norm);
                  setSavedDraft(norm);
                  setPublished(norm);
                }}
              />
            ) : (
              <>
                {section.images && section.images.length > 0 && (
                  <div className="space-y-3 mb-6">
                    {section.images.map((img) => (
                      <ImageUploader
                        key={img.slot}
                        slot={img.slot}
                        label={img.label}
                        description={img.description}
                        recommended={img.recommended}
                        meta={draft.images?.[img.slot] || {}}
                        onUpdated={(next) => {
                          // ImageUploader hits the backend synchronously,
                          // so both draft and savedDraft can be advanced.
                          const patcher = (d) => (d ? {
                            ...d,
                            images: { ...(d.images || {}), [img.slot]: next },
                          } : d);
                          setDraft(patcher);
                          setSavedDraft(patcher);
                        }}
                      />
                    ))}
                  </div>
                )}
                <TranslationFields
                  fields={section.fields}
                  lang={activeLang}
                  values={draft.translations[activeLang] || {}}
                  onChange={(key, val) => updateTranslation(activeLang, key, val)}
                  sectionId={section.id}
                  toggles={draft.toggles}
                  onToggle={updateToggle}
                  extraToggles={section.toggles}
                />
              </>
            )}
          </div>
        </div>
      </div>
      <PreviewModal open={previewOpen} onClose={() => setPreviewOpen(false)} target={previewTarget} />
    </div>
  );
}

function StatusBadge({ dirty, draftAhead, updatedAt, version }) {
  return (
    <div className="flex items-center gap-3 text-xs">
      {dirty ? (
        <span className="inline-flex items-center gap-1.5 text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1 font-semibold" data-testid="landing-status-dirty">
          <AlertCircle className="w-3.5 h-3.5" /> Unsaved changes
        </span>
      ) : draftAhead ? (
        <span className="inline-flex items-center gap-1.5 text-sky-700 bg-sky-50 border border-sky-200 rounded-full px-2.5 py-1 font-semibold" data-testid="landing-status-draft-saved">
          <Check className="w-3.5 h-3.5" /> Draft saved — not yet published
        </span>
      ) : (
        <span className="inline-flex items-center gap-1.5 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1 font-semibold" data-testid="landing-status-clean">
          <Check className="w-3.5 h-3.5" /> In sync with published
        </span>
      )}
      {updatedAt && (
        <span className="text-slate-500">
          Last edit: {new Date(updatedAt).toLocaleString()}
        </span>
      )}
      {version && (
        <span className="text-slate-400 hidden sm:inline">v{version.slice(0, 8)}</span>
      )}
    </div>
  );
}

function Btn({ onClick, icon: Icon, children, busy, variant = "outline", testid }) {
  const base = "inline-flex items-center gap-2 h-10 px-3.5 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed";
  const styles = {
    primary: "bg-[#0F62FE] text-white hover:bg-[#0050E6]",
    outline: "border border-slate-300 bg-white text-slate-700 hover:bg-slate-50",
    ghost: "text-slate-600 hover:bg-slate-100",
  }[variant];
  return (
    <button type="button" onClick={onClick} disabled={busy} className={`${base} ${styles}`} data-testid={testid}>
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : Icon ? <Icon className="w-4 h-4" /> : null}
      {children}
    </button>
  );
}

function LanguageTabs({ value, onChange }) {
  return (
    <div className="inline-flex items-center rounded-lg border border-slate-200 bg-slate-50 p-1" data-testid="landing-lang-tabs">
      {CMS_LANGUAGES.map((l) => (
        <button
          key={l.code}
          type="button"
          onClick={() => onChange(l.code)}
          className={`inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md text-xs font-semibold transition-colors ${
            value === l.code
              ? "bg-white shadow text-[#0F62FE]"
              : "text-slate-500 hover:text-slate-800"
          }`}
          data-testid={`landing-lang-${l.code}`}
        >
          <FlagIcon code={l.flag} />
          <span className="hidden sm:inline">{l.label}</span>
        </button>
      ))}
    </div>
  );
}

function TranslationFields({ fields, lang, values, onChange, sectionId, toggles, onToggle, extraToggles }) {
  const toggleKey = sectionId === "login" ? null : sectionId;
  const sectionEnabled = toggleKey ? toggles[toggleKey] !== false : true;
  return (
    <div className="space-y-5" data-testid="landing-translation-fields">
      {toggleKey && (
        <label className="flex items-center gap-2 text-sm select-none" data-testid={`landing-toggle-${toggleKey}`}>
          <input
            type="checkbox"
            checked={sectionEnabled}
            onChange={(e) => onToggle(toggleKey, e.target.checked)}
            className="h-4 w-4 accent-[#0F62FE]"
          />
          <span className="font-semibold text-slate-800">Show this section on the landing page</span>
        </label>
      )}
      {fields.map((f) => (
        <div key={f.path} className="space-y-1.5">
          <label className="block text-xs font-bold uppercase tracking-widest text-slate-500">
            {f.label}
            <span className="ml-2 font-mono normal-case tracking-normal text-slate-300">{f.path}</span>
          </label>
          {f.hint && <p className="text-xs text-slate-500">{f.hint}</p>}
          {f.type === "textarea" ? (
            <textarea
              value={values[f.path] ?? ""}
              placeholder={lang === "en" ? f.defaultEn : `Default (English): ${f.defaultEn}`}
              onChange={(e) => onChange(f.path, e.target.value)}
              rows={3}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
              data-testid={`landing-field-${f.path.replace(/\./g, "-")}`}
            />
          ) : (
            <input
              type="text"
              value={values[f.path] ?? ""}
              placeholder={lang === "en" ? f.defaultEn : `Default (English): ${f.defaultEn}`}
              onChange={(e) => onChange(f.path, e.target.value)}
              className="w-full h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
              data-testid={`landing-field-${f.path.replace(/\./g, "-")}`}
            />
          )}
        </div>
      ))}
      {/* Extra non-translation toggles declared by the section schema.
          Rendered below the translation fields so a promo/announcement
          toggle sits right under the promo text field. */}
      {Array.isArray(extraToggles) && extraToggles.length > 0 && (
        <div
          className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 space-y-3"
          data-testid={`landing-extra-toggles-${sectionId}`}
        >
          {extraToggles.map((tg) => (
            <label
              key={tg.key}
              className="flex items-start gap-2 text-sm select-none cursor-pointer"
              data-testid={`landing-toggle-${tg.key}-label`}
            >
              <input
                type="checkbox"
                checked={toggles[tg.key] !== false && toggles[tg.key] !== undefined ? !!toggles[tg.key] : false}
                onChange={(e) => onToggle(tg.key, e.target.checked)}
                className="h-4 w-4 accent-[#0F62FE] mt-0.5"
                data-testid={`landing-toggle-${tg.key}`}
              />
              <span className="flex-1">
                <span className="font-semibold text-slate-800 block">{tg.label}</span>
                {tg.description && (
                  <span className="text-xs text-slate-500 block mt-0.5">{tg.description}</span>
                )}
              </span>
            </label>
          ))}
        </div>
      )}
      <p className="text-xs text-slate-400 pt-3 border-t border-slate-100">
        Tip · leave a field empty to fall back to the bundled default translation.
      </p>
    </div>
  );
}

function GlobalBrandingEditor({ draft, update }) {
  // Feb 2026 refactor: Global Branding is now a slim wrapper that renders
  // only the Identity editor. The former Logo & Visuals, Public Landing
  // Page and Social Apps sub-tabs have been promoted to top-level sidebar
  // entries (`visuals`, `landing_stats`) or point to the pre-existing
  // top-level `social` section — see /app/frontend/src/lib/landingSchema.js.
  return (
    <div className="space-y-5" data-testid="landing-global-editor">
      <IdentityTab draft={draft} update={update} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Identity — merges the CMS "branding" translations block with the legacy
// /admin/branding record (system name, tagline, WhatsApp support).
// ---------------------------------------------------------------------------
function IdentityTab({ draft, update }) {
  const { brand, refresh } = useBranding();
  const b = draft.branding || {};
  const [sysName, setSysName] = useState("");
  const [sysTagline, setSysTagline] = useState("");
  const [wa, setWa] = useState("");
  const [supportEmail, setSupportEmail] = useState("");
  const [emailStatus, setEmailStatus] = useState({ configured: false, email_provider_ready: false, loaded: false });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  useEffect(() => {
    setSysName(brand.brand_name || "");
    setSysTagline(brand.brand_tagline || "");
    setWa(brand.whatsapp_number || "");
  }, [brand]);
  // Fetch the current support e-mail (admin-only endpoint). It's not
  // included in /branding for privacy, so we hit the dedicated route.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/admin/branding/support-email");
        if (!cancelled) {
          setSupportEmail(data.support_email || "");
          setEmailStatus({
            configured: !!data.configured,
            email_provider_ready: !!data.email_provider_ready,
            loaded: true,
          });
        }
      } catch {
        if (!cancelled) setEmailStatus((s) => ({ ...s, loaded: true }));
      }
    })();
    return () => { cancelled = true; };
  }, []);
  const saveSystemBranding = async () => {
    if (!sysName.trim()) {
      toast.error("System name is required");
      return;
    }
    setSaving(true);
    try {
      await api.put("/admin/branding", {
        brand_name: sysName.trim(),
        brand_tagline: sysTagline.trim(),
        whatsapp_number: wa.trim(),
        support_email: supportEmail.trim(),
      });
      await refresh();
      // Refresh the support-email status card too.
      try {
        const { data } = await api.get("/admin/branding/support-email");
        setEmailStatus({
          configured: !!data.configured,
          email_provider_ready: !!data.email_provider_ready,
          loaded: true,
        });
      } catch { /* ignore — the save itself succeeded */ }
      toast.success("System branding saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };
  const runTestEmail = async () => {
    if (!supportEmail.trim()) {
      toast.error("Save a support e-mail first.");
      return;
    }
    setTesting(true);
    try {
      const { data } = await api.post("/admin/branding/test-email", {});
      if (data.status === "accepted") {
        toast.success("Test e-mail accepted by the provider.");
      } else {
        toast.error("The provider did not confirm delivery. Please check the configuration.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test e-mail failed.");
    } finally {
      setTesting(false);
    }
  };
  return (
    <div className="space-y-6" data-testid="global-branding-identity">
      {/* System-wide identity (drives sidebar, receipts, PDFs, notifications) */}
      <section
        className="rounded-xl border border-slate-200 bg-white p-5"
        data-testid="branding-text-card"
      >
        <div className="text-[10px] font-bold uppercase tracking-widest text-[#0F62FE] flex items-center gap-1.5 mb-1">
          <Type className="w-3 h-3" /> System identity
        </div>
        <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          System name &amp; support
        </h3>
        <p className="text-xs text-slate-500 mt-1">
          These values drive the sidebar title, PDF receipts, e-mails and the
          browser tab title. They are read from <code>/admin/branding</code>.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
              System name
            </label>
            <input
              value={sysName}
              onChange={(e) => setSysName(e.target.value)}
              maxLength={64}
              className="flex h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
              data-testid="branding-name-input"
              placeholder="My GlobalTopup"
            />
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
              Tagline (sidebar subtitle)
            </label>
            <input
              value={sysTagline}
              onChange={(e) => setSysTagline(e.target.value)}
              maxLength={64}
              className="flex h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
              data-testid="branding-tagline-input"
              placeholder="Reseller Portal"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
              WhatsApp support number (with country code)
            </label>
            <input
              value={wa}
              onChange={(e) => setWa(e.target.value)}
              maxLength={32}
              className="flex h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
              data-testid="branding-whatsapp-input"
              placeholder="+56 9 5929 0754"
            />
            {wa && (
              <a
                href={whatsappHref(wa, "Hello, test message from Branding Settings")}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 mt-2 text-xs text-emerald-700 font-semibold hover:underline"
                data-testid="branding-whatsapp-test"
              >
                Test WhatsApp link ↗
              </a>
            )}
          </div>
          {/* Contact / Support Email — receives every message from the
              public contact form. Validated for shape on both sides.
              The address is admin-only and never rendered on the public
              landing page. */}
          <div className="sm:col-span-2">
            <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
              Contact / Support Email
            </label>
            <p className="text-[11px] text-slate-500 mb-2">
              Destination for messages submitted via the public contact form. Kept private — never displayed on the landing page.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="email"
                value={supportEmail}
                onChange={(e) => setSupportEmail(e.target.value)}
                maxLength={254}
                className="flex-1 h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
                data-testid="branding-support-email-input"
                placeholder="support@myglobaltopup.com"
              />
              <button
                type="button"
                onClick={runTestEmail}
                disabled={testing || !emailStatus.configured}
                data-testid="branding-test-email-btn"
                className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-[#0F62FE] text-[#0F62FE] text-sm font-semibold hover:bg-[#0F62FE]/5 disabled:opacity-40 disabled:cursor-not-allowed"
                title={emailStatus.configured ? "Send a controlled test message" : "Save the e-mail first, then run a test"}
              >
                {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Test Email
              </button>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
              {emailStatus.loaded ? (
                <>
                  <span
                    className={
                      "inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold " +
                      (emailStatus.configured
                        ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                        : "bg-amber-50 text-amber-700 border border-amber-200")
                    }
                    data-testid="branding-support-email-status"
                  >
                    {emailStatus.configured ? "Email configured" : "Not configured"}
                  </span>
                  <span
                    className={
                      "inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold " +
                      (emailStatus.email_provider_ready
                        ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                        : "bg-rose-50 text-rose-700 border border-rose-200")
                    }
                    data-testid="branding-email-provider-status"
                  >
                    {emailStatus.email_provider_ready
                      ? "Provider ready"
                      : "Provider key missing (set RESEND_API_KEY in production secrets)"}
                  </span>
                </>
              ) : (
                <span className="text-slate-400">Loading status…</span>
              )}
            </div>
          </div>
        </div>
        <div className="mt-5 flex justify-end">
          <button
            onClick={saveSystemBranding}
            disabled={saving}
            data-testid="branding-save-btn"
            className="inline-flex items-center gap-2 h-10 px-5 rounded-lg bg-[#0F62FE] text-white text-sm font-semibold hover:bg-[#0050E6] disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save
          </button>
        </div>
      </section>

      {/* CMS branding (drives landing page copy via the {{brand}} placeholder) */}
      <section
        className="rounded-xl border border-slate-200 bg-white p-5"
        data-testid="landing-brand-card"
      >
        <div className="text-[10px] font-bold uppercase tracking-widest text-[#0F62FE] flex items-center gap-1.5 mb-1">
          <Palette className="w-3 h-3" /> Landing page identity (CMS)
        </div>
        <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          Landing brand name &amp; default language
        </h3>
        <p className="text-xs text-slate-500 mt-1">
          These are the CMS-scoped overrides applied via draft → publish. The
          <code className="mx-1">{"{{brand}}"}</code> placeholder in translations resolves to the
          value below.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
              Landing brand name
            </label>
            <input
              type="text"
              value={b.brand_name || ""}
              placeholder="My GlobalTopup"
              onChange={(e) => update("brand_name", e.target.value)}
              className="w-full h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
              data-testid="landing-brand-name"
            />
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
              Landing tagline
            </label>
            <input
              type="text"
              value={b.brand_tagline || ""}
              placeholder="Reseller Portal"
              onChange={(e) => update("brand_tagline", e.target.value)}
              className="w-full h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
              data-testid="landing-brand-tagline"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
              Default language
            </label>
            <p className="text-xs text-slate-500 mb-1.5">
              The language shown to first-time visitors. Users can always switch via the language picker.
            </p>
            <div className="relative">
              <select
                value={b.default_language || "en"}
                onChange={(e) => update("default_language", e.target.value)}
                className="w-full h-10 rounded-lg border border-slate-300 bg-white px-3 pr-8 text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
                data-testid="landing-default-language"
              >
                {CMS_LANGUAGES.map((l) => (
                  <option key={l.code} value={l.code}>{l.label}</option>
                ))}
              </select>
              <ChevronDown className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>
          </div>
        </div>
        <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
          <div className="flex items-center gap-2 text-slate-700 text-xs font-bold">
            <Languages className="w-3.5 h-3.5" /> Multi-language editing
          </div>
          <p className="text-[11px] text-slate-500 mt-1">
            Home, About, Services, Products, Partners, Contact, Footer and Login copy is editable in EN · ES · FR · HT from the sections on the left.
          </p>
        </div>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Logo & Visuals — the main brand logo, backed by /admin/branding/logo.
// The "Login welcome image" widget was intentionally removed — the same
// slot lives in the CMS "Login Page" section (login_office) and there
// must be only one editor for it (spec §6).
// ---------------------------------------------------------------------------
function LogoVisualsTab() {
  const { brand, refresh } = useBranding();
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      toast.error("Image is too large (max 3 MB)");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/admin/branding/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await refresh();
      toast.success("Logo updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    setBusy(true);
    try {
      await api.delete("/admin/branding/logo");
      await refresh();
      toast.success("Logo removed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };
  const currentUrl = brand.brand_logo_url;
  return (
    <section
      className="rounded-xl border border-slate-200 bg-white p-5"
      data-testid="global-branding-visuals"
    >
      <div className="text-[10px] font-bold uppercase tracking-widest text-[#0F62FE] flex items-center gap-1.5 mb-1">
        <Palette className="w-3 h-3" /> Visual
      </div>
      <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
        Main logo
      </h3>
      <p className="text-slate-500 text-xs mt-1">
        Shown in the sidebar, on the login page, in PDFs, receipts and certificates. PNG or JPEG, max 3 MB, resized to 512 × 512.
      </p>
      <div className="mt-4 flex flex-col sm:flex-row items-start gap-5">
        <div
          className="shrink-0 w-24 h-24 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden"
          data-testid="branding-logo-preview"
        >
          {currentUrl ? (
            <img src={currentUrl} alt="logo" className="w-full h-full object-contain" />
          ) : (
            <ImageIcon className="w-8 h-8 text-slate-300" />
          )}
        </div>
        <div className="flex-1 flex flex-wrap gap-2 items-center">
          <input
            id="branding-logo-file"
            type="file"
            accept="image/*"
            onChange={(e) => upload(e.target.files?.[0])}
            className="hidden"
            data-testid="branding-logo-input"
          />
          <label
            htmlFor="branding-logo-file"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#0F62FE] text-white text-sm font-semibold hover:bg-[#0050E6] cursor-pointer"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
            {currentUrl ? "Replace" : "Upload"}
          </label>
          {currentUrl && (
            <button
              type="button"
              onClick={remove}
              disabled={busy}
              data-testid="branding-logo-remove"
              className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-slate-300 text-rose-600 text-sm font-semibold hover:bg-rose-50 disabled:opacity-50"
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </button>
          )}
        </div>
      </div>
      <div className="mt-6 rounded-lg border border-dashed border-slate-200 bg-slate-50 p-3">
        <div className="text-[11px] font-semibold text-slate-500">
          Login backdrop image
        </div>
        <p className="text-[11px] text-slate-500 mt-1">
          Manage the login page background from <b>Login Page → Login office image</b> in the sections list on the left. Only one editor is available for this slot.
        </p>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Public Landing Page — landing_stats block on /admin/branding.
// Fully preserved from the old page; only the visual container changes.
// ---------------------------------------------------------------------------
function PublicLandingTab() {
  const { brand, refresh } = useBranding();
  const [statsEnabled, setStatsEnabled] = useState(true);
  const [stats, setStats] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setStatsEnabled(brand.landing_stats_section_enabled !== false);
    setStats(Array.isArray(brand.landing_stats) ? brand.landing_stats.map((s) => ({ ...s })) : []);
  }, [brand]);
  const update = (idx, patch) =>
    setStats((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/branding", {
        landing_stats_section_enabled: statsEnabled,
        landing_stats: stats.map((s) => ({
          id: s.id,
          label: s.label || "",
          value: s.value || "",
          enabled: !!s.enabled,
        })),
      });
      await refresh();
      toast.success("Landing page statistics saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };
  const inputCls =
    "flex h-10 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE]";
  return (
    <section
      className="rounded-xl border border-slate-200 bg-white p-5"
      data-testid="branding-landing-stats-card"
    >
      <div className="text-[10px] font-bold uppercase tracking-widest text-[#0F62FE] flex items-center gap-1.5 mb-1">
        <BarChart3 className="w-3 h-3" /> Landing page
      </div>
      <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
        Landing Page Statistics
      </h3>
      <p className="text-slate-500 text-xs mt-1 max-w-2xl">
        Numbers shown on the public homepage. Each stat is hidden until you enable it and enter a confirmed value (e.g. &ldquo;400+&rdquo;, &ldquo;70+&rdquo;, &ldquo;24/7&rdquo;). The entire strip can be turned off with the toggle below.
      </p>

      <label
        className="flex items-center gap-2 mt-5 cursor-pointer"
        data-testid="landing-stats-section-toggle-label"
      >
        <input
          type="checkbox"
          checked={statsEnabled}
          onChange={(e) => setStatsEnabled(e.target.checked)}
          className="w-4 h-4 rounded border-slate-300"
          data-testid="landing-stats-section-toggle"
        />
        <span className="text-sm font-medium text-slate-800">
          {statsEnabled
            ? "Section visible on homepage"
            : "Section hidden — no statistics will appear"}
        </span>
      </label>

      <div className="mt-5 space-y-3">
        {stats.map((s, idx) => (
          <div
            key={s.id}
            className={
              "grid grid-cols-1 md:grid-cols-[auto_1fr_1fr_auto] gap-3 items-center rounded-lg border p-3 " +
              (s.enabled ? "border-[#0F62FE]/30 bg-blue-50/40" : "border-slate-200 bg-white")
            }
            data-testid={`landing-stat-row-${s.id}`}
          >
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!s.enabled}
                onChange={(e) => update(idx, { enabled: e.target.checked })}
                className="w-4 h-4 rounded border-slate-300"
                data-testid={`landing-stat-toggle-${s.id}`}
              />
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 min-w-[70px]">
                {s.id}
              </span>
            </label>
            <input
              value={s.label || ""}
              onChange={(e) => update(idx, { label: e.target.value })}
              maxLength={64}
              className={inputCls}
              placeholder="Label (e.g. Operators)"
              data-testid={`landing-stat-label-${s.id}`}
            />
            <input
              value={s.value || ""}
              onChange={(e) => update(idx, { value: e.target.value })}
              maxLength={32}
              className={inputCls}
              placeholder="Value (e.g. 400+, 24/7)"
              data-testid={`landing-stat-value-${s.id}`}
            />
            <div
              className={
                "text-xs font-semibold " +
                (s.enabled && s.value?.trim() ? "text-emerald-600" : "text-slate-400")
              }
            >
              {s.enabled && s.value?.trim() ? "Shown" : "Hidden"}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-5 flex justify-end">
        <button
          onClick={save}
          disabled={saving}
          data-testid="branding-save-stats-btn"
          className="inline-flex items-center gap-2 h-10 px-5 rounded-lg bg-[#0F62FE] text-white text-sm font-semibold hover:bg-[#0050E6] disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save
        </button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Partners / operators editor
// ---------------------------------------------------------------------------
function PartnersEditor({ partners, onChange, translationFields, activeLangTabs }) {
  const [busy, setBusy] = useState(false);
  const [newName, setNewName] = useState("");
  const [newCountry, setNewCountry] = useState("");

  const create = async () => {
    if (!newName.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/landing-settings/admin/partners", {
        name: newName.trim(),
        country: newCountry.trim() || null,
        enabled: true,
        has_logo_rights: false,
        order: partners.length,
      });
      onChange([...partners, data]);
      setNewName("");
      setNewCountry("");
      toast.success("Operator added");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add");
    } finally {
      setBusy(false);
    }
  };

  const update = async (partnerId, patch) => {
    const p = partners.find((x) => x.id === partnerId);
    if (!p) return;
    const next = { ...p, ...patch };
    onChange(partners.map((x) => (x.id === partnerId ? next : x)));
    try {
      await api.put(`/landing-settings/admin/partners/${partnerId}`, {
        name: next.name,
        country: next.country || null,
        brand_color: next.brand_color || null,
        enabled: next.enabled,
        has_logo_rights: next.has_logo_rights,
        order: next.order,
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save operator");
    }
  };

  const remove = async (partnerId) => {
    if (!confirm("Delete this operator?")) return;
    try {
      await api.delete(`/landing-settings/admin/partners/${partnerId}`);
      onChange(partners.filter((x) => x.id !== partnerId));
      toast.success("Operator removed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  const uploadLogo = async (partnerId, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/landing-settings/admin/partners/${partnerId}/logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(partners.map((x) => (x.id === partnerId ? { ...x, logo_asset_id: data.asset_id } : x)));
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Logo upload failed");
    }
  };

  const removeLogo = async (partnerId) => {
    try {
      await api.delete(`/landing-settings/admin/partners/${partnerId}/logo`);
      onChange(partners.map((x) => (x.id === partnerId ? { ...x, logo_asset_id: null } : x)));
      toast.success("Logo removed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove logo");
    }
  };

  return (
    <div className="space-y-6" data-testid="partners-editor">
      {/* Section headline + disclaimer translations */}
      <div>
        <div className="flex justify-end mb-3">{activeLangTabs}</div>
        {translationFields}
      </div>

      {/* Operator list */}
      <div className="border-t border-slate-200 pt-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-slate-800">Operators</h3>
          <span className="text-xs text-slate-400">{partners.length} total</span>
        </div>

        {/* Add new operator */}
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 flex flex-wrap gap-2 mb-3" data-testid="partner-add-row">
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Operator name (e.g. Digicel)"
            className="flex-1 min-w-[160px] h-9 rounded-md border border-slate-300 bg-white px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
            data-testid="partner-add-name"
          />
          <input
            type="text"
            value={newCountry}
            onChange={(e) => setNewCountry(e.target.value)}
            placeholder="Country (optional)"
            className="w-40 h-9 rounded-md border border-slate-300 bg-white px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
            data-testid="partner-add-country"
          />
          <button
            type="button"
            onClick={create}
            disabled={busy || !newName.trim()}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-[#0F62FE] text-white text-xs font-semibold hover:bg-[#0050E6] disabled:opacity-50"
            data-testid="partner-add-btn"
          >
            <Plus className="w-3.5 h-3.5" /> Add operator
          </button>
        </div>

        {partners.length === 0 ? (
          <div className="text-sm text-slate-500 py-6 text-center border border-dashed border-slate-200 rounded-lg">
            No operators yet — add your first one above.
          </div>
        ) : (
          <div className="space-y-3">
            {partners.map((p, idx) => (
              <PartnerRow
                key={p.id}
                partner={p}
                index={idx}
                total={partners.length}
                onUpdate={(patch) => update(p.id, patch)}
                onRemove={() => remove(p.id)}
                onUploadLogo={(f) => uploadLogo(p.id, f)}
                onRemoveLogo={() => removeLogo(p.id)}
                onReorder={(dir) => {
                  const target = idx + dir;
                  if (target < 0 || target >= partners.length) return;
                  const swapped = [...partners];
                  [swapped[idx], swapped[target]] = [swapped[target], swapped[idx]];
                  // update order field for both
                  swapped.forEach((x, i) => { x.order = i; });
                  onChange(swapped);
                  // persist both
                  swapped.forEach((x) => {
                    api.put(`/landing-settings/admin/partners/${x.id}`, {
                      name: x.name, country: x.country || null, brand_color: x.brand_color || null,
                      enabled: x.enabled, has_logo_rights: x.has_logo_rights, order: x.order,
                    }).catch(() => {});
                  });
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PartnerRow({ partner, index, total, onUpdate, onRemove, onUploadLogo, onRemoveLogo, onReorder }) {
  const fileRef = useRef(null);
  const logoUrl = partner.logo_asset_id
    ? `${process.env.REACT_APP_BACKEND_URL || ""}/api/landing-settings/asset/${partner.logo_asset_id}`
    : null;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`partner-row-${partner.id}`}>
      <div className="flex items-start gap-3">
        <div className="flex flex-col items-center gap-1 pt-1">
          <button
            type="button"
            onClick={() => onReorder(-1)}
            disabled={index === 0}
            className="text-slate-400 hover:text-slate-600 disabled:opacity-30 text-xs"
            aria-label="Move up"
          >▲</button>
          <GripVertical className="w-3.5 h-3.5 text-slate-300" />
          <button
            type="button"
            onClick={() => onReorder(1)}
            disabled={index === total - 1}
            className="text-slate-400 hover:text-slate-600 disabled:opacity-30 text-xs"
            aria-label="Move down"
          >▼</button>
        </div>

        <div className="w-16 h-16 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
          {logoUrl ? (
            <img src={logoUrl} alt={partner.name} className="max-w-full max-h-full object-contain" />
          ) : (
            <span className="text-[10px] font-bold text-slate-400">{partner.name.slice(0, 3).toUpperCase()}</span>
          )}
        </div>

        <div className="flex-1 grid gap-2 sm:grid-cols-3">
          <input
            type="text"
            value={partner.name}
            onChange={(e) => onUpdate({ name: e.target.value })}
            placeholder="Operator name"
            className="h-9 rounded-md border border-slate-300 bg-white px-2.5 text-sm sm:col-span-1 focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
            data-testid={`partner-name-${partner.id}`}
          />
          <input
            type="text"
            value={partner.country || ""}
            onChange={(e) => onUpdate({ country: e.target.value })}
            placeholder="Country"
            className="h-9 rounded-md border border-slate-300 bg-white px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
            data-testid={`partner-country-${partner.id}`}
          />
          <input
            type="text"
            value={partner.brand_color || ""}
            onChange={(e) => onUpdate({ brand_color: e.target.value })}
            placeholder="Brand color (#RRGGBB)"
            className="h-9 rounded-md border border-slate-300 bg-white px-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
            data-testid={`partner-color-${partner.id}`}
          />

          <label className="flex items-center gap-2 text-xs text-slate-700 sm:col-span-3">
            <input
              type="checkbox"
              checked={partner.enabled !== false}
              onChange={(e) => onUpdate({ enabled: e.target.checked })}
              className="h-4 w-4 accent-[#0F62FE]"
              data-testid={`partner-enabled-${partner.id}`}
            />
            <span>Show on public landing page</span>
          </label>
        </div>

        <div className="flex flex-col gap-1 items-end">
          <input
            ref={fileRef}
            type="file"
            accept="image/webp,image/png,image/jpeg,image/jpg"
            onChange={(e) => onUploadLogo(e.target.files?.[0])}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="inline-flex items-center gap-1 h-7 px-2 rounded-md bg-[#0F62FE] text-white text-[10px] font-bold hover:bg-[#0050E6]"
            data-testid={`partner-upload-${partner.id}`}
            title="Upload logo"
          >
            <ImageIcon className="w-3 h-3" /> Logo
          </button>
          {logoUrl && (
            <button
              type="button"
              onClick={onRemoveLogo}
              className="text-[10px] text-slate-500 hover:text-rose-600"
              data-testid={`partner-remove-logo-${partner.id}`}
            >
              Remove logo
            </button>
          )}
          <button
            type="button"
            onClick={onRemove}
            className="inline-flex items-center gap-1 h-7 px-2 rounded-md border border-slate-300 text-rose-600 text-[10px] font-bold hover:bg-rose-50"
            data-testid={`partner-delete-${partner.id}`}
          >
            <Trash2 className="w-3 h-3" /> Delete
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Legal editor (Privacy + Terms) with per-language body
// ---------------------------------------------------------------------------
function LegalEditor({ lang, legal, onChange }) {
  const [activePage, setActivePage] = useState("privacy");
  const [busy, setBusy] = useState(false);
  const entry = (legal?.[activePage] || {})[lang] || { title: "", body: "", updated_at: null };

  const update = (patch) => onChange(activePage, { ...entry, ...patch });

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/landing-settings/admin/legal/${activePage}`, {
        [lang]: { title: entry.title || "", body: entry.body || "" },
      });
      toast.success("Legal page saved (draft)");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save legal page");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="legal-editor">
      <div className="inline-flex items-center rounded-lg border border-slate-200 bg-slate-50 p-1">
        {[{ id: "privacy", label: "Privacy Policy" }, { id: "terms", label: "Terms & Conditions" }].map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => setActivePage(p.id)}
            className={`inline-flex items-center h-8 px-3 rounded-md text-xs font-semibold transition-colors ${
              activePage === p.id ? "bg-white shadow text-[#0F62FE]" : "text-slate-500 hover:text-slate-800"
            }`}
            data-testid={`legal-page-${p.id}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="space-y-1.5">
        <label className="block text-xs font-bold uppercase tracking-widest text-slate-500">
          Page title ({lang.toUpperCase()})
        </label>
        <input
          type="text"
          value={entry.title || ""}
          onChange={(e) => update({ title: e.target.value })}
          placeholder={activePage === "privacy" ? "Privacy Policy" : "Terms & Conditions"}
          className="w-full h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
          data-testid={`legal-title-${activePage}-${lang}`}
        />
      </div>

      <div className="space-y-1.5">
        <label className="block text-xs font-bold uppercase tracking-widest text-slate-500">
          Body ({lang.toUpperCase()})
        </label>
        <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-block">
          Content shown as-is on the public page. Use blank lines to separate paragraphs.
        </p>
        <textarea
          value={entry.body || ""}
          onChange={(e) => update({ body: e.target.value })}
          rows={16}
          placeholder="Paste your legal text here…"
          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
          data-testid={`legal-body-${activePage}-${lang}`}
        />
        <div className="flex items-center justify-between text-[11px] text-slate-400">
          <span>{(entry.body || "").length.toLocaleString()} characters</span>
          {entry.updated_at && <span>Last saved: {new Date(entry.updated_at).toLocaleString()}</span>}
        </div>
      </div>

      <div>
        <button
          type="button"
          onClick={save}
          disabled={busy}
          className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#0F62FE] text-white text-sm font-semibold hover:bg-[#0050E6] disabled:opacity-50"
          data-testid={`legal-save-${activePage}-${lang}`}
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save {activePage === "privacy" ? "Privacy Policy" : "Terms & Conditions"} ({lang.toUpperCase()})
        </button>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Design & Layout editor (Phase 3): colours, opacity, section order.
// ---------------------------------------------------------------------------
const REORDERABLE_SECTIONS = [
  { id: "hero", label: "Home / Hero" },
  { id: "about", label: "About" },
  { id: "services", label: "Services" },
  { id: "products", label: "Products" },
  { id: "statistics", label: "Statistics" },
  { id: "partners", label: "Partners" },
  { id: "contact", label: "Contact / CTA" },
];

function DesignEditor({ design, toggles, onChangeDesign, onChangeToggle }) {
  const order = design.section_order && design.section_order.length ? design.section_order : REORDERABLE_SECTIONS.map((s) => s.id);
  const reorder = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= order.length) return;
    const next = [...order];
    [next[idx], next[target]] = [next[target], next[idx]];
    onChangeDesign({ section_order: next });
  };
  return (
    <div className="space-y-6" data-testid="design-editor">
      <div>
        <h3 className="text-sm font-bold text-slate-800 mb-3">Brand colours</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          {[
            { key: "primary_color", label: "Primary color", hint: "Buttons, links, active states" },
            { key: "secondary_color", label: "Secondary color", hint: "Optional secondary tone" },
            { key: "accent_color", label: "Accent color", hint: "Highlights & hover states" },
            { key: "cta_button_color", label: "CTA button color", hint: "Primary call-to-action buttons" },
          ].map((c) => (
            <div key={c.key} className="space-y-1.5">
              <label className="block text-xs font-bold uppercase tracking-widest text-slate-500">{c.label}</label>
              <p className="text-xs text-slate-500">{c.hint}</p>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={design[c.key] || "#0F62FE"}
                  onChange={(e) => onChangeDesign({ [c.key]: e.target.value })}
                  className="h-10 w-14 rounded-md border border-slate-300 bg-white cursor-pointer"
                  data-testid={`design-${c.key}-picker`}
                />
                <input
                  type="text"
                  value={design[c.key] || ""}
                  placeholder="#RRGGBB"
                  onChange={(e) => onChangeDesign({ [c.key]: e.target.value })}
                  className="flex-1 h-10 rounded-md border border-slate-300 bg-white px-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
                  data-testid={`design-${c.key}-text`}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-sm font-bold text-slate-800 mb-3">Hero overlay</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <label className="block text-xs font-bold uppercase tracking-widest text-slate-500">Overlay color</label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={design.hero_overlay_color || "#001A66"}
                onChange={(e) => onChangeDesign({ hero_overlay_color: e.target.value })}
                className="h-10 w-14 rounded-md border border-slate-300 bg-white cursor-pointer"
                data-testid="design-overlay-color-picker"
              />
              <input
                type="text"
                value={design.hero_overlay_color || ""}
                placeholder="#001A66"
                onChange={(e) => onChangeDesign({ hero_overlay_color: e.target.value })}
                className="flex-1 h-10 rounded-md border border-slate-300 bg-white px-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="block text-xs font-bold uppercase tracking-widest text-slate-500">
              Overlay opacity <span className="text-slate-400 font-normal">({Math.round((design.hero_overlay_opacity ?? 0.55) * 100)}%)</span>
            </label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={design.hero_overlay_opacity ?? 0.55}
              onChange={(e) => onChangeDesign({ hero_overlay_opacity: parseFloat(e.target.value) })}
              className="w-full accent-[#0F62FE]"
              data-testid="design-overlay-opacity"
            />
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-sm font-bold text-slate-800 mb-3">Section order & visibility</h3>
        <p className="text-xs text-slate-500 mb-3">Reorder or hide the sections that appear on the public landing page.</p>
        <div className="space-y-2" data-testid="design-section-order">
          {order.map((id, idx) => {
            const sec = REORDERABLE_SECTIONS.find((s) => s.id === id);
            if (!sec) return null;
            const enabled = toggles[id] !== false;
            return (
              <div key={id} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-slate-50" data-testid={`design-section-row-${id}`}>
                <div className="flex flex-col text-slate-400">
                  <button type="button" onClick={() => reorder(idx, -1)} disabled={idx === 0} className="text-xs hover:text-slate-700 disabled:opacity-30" aria-label="Up">▲</button>
                  <GripVertical className="w-3.5 h-3.5" />
                  <button type="button" onClick={() => reorder(idx, 1)} disabled={idx === order.length - 1} className="text-xs hover:text-slate-700 disabled:opacity-30" aria-label="Down">▼</button>
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-sm text-slate-800">{sec.label}</div>
                  <div className="text-[11px] text-slate-400">Position #{idx + 1}</div>
                </div>
                <label className="inline-flex items-center gap-2 text-xs text-slate-700">
                  <input
                    type="checkbox"
                    checked={enabled}
                    onChange={(e) => onChangeToggle(id, e.target.checked)}
                    className="h-4 w-4 accent-[#0F62FE]"
                    data-testid={`design-section-toggle-${id}`}
                  />
                  <span>Visible</span>
                </label>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SEO editor (Phase 3): per-language title/description + canonical + robots
// ---------------------------------------------------------------------------
function SeoEditor({ lang, seo, onChange, onChangeLang }) {
  const perLang = seo?.per_language?.[lang] || {};
  return (
    <div className="space-y-6" data-testid="seo-editor">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <label className="block text-xs font-bold uppercase tracking-widest text-slate-500">Canonical URL</label>
          <input
            type="url"
            value={seo?.canonical_url || ""}
            onChange={(e) => onChange({ canonical_url: e.target.value })}
            placeholder="https://myglobaltopup.com/"
            className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
            data-testid="seo-canonical"
          />
        </div>
        <div className="space-y-1.5">
          <label className="flex items-center gap-2 text-xs text-slate-700 mt-6">
            <input
              type="checkbox"
              checked={seo?.allow_indexing !== false}
              onChange={(e) => onChange({ allow_indexing: e.target.checked })}
              className="h-4 w-4 accent-[#0F62FE]"
              data-testid="seo-allow-indexing"
            />
            <span className="font-semibold">Allow search engines to index this site</span>
          </label>
          <p className="text-[11px] text-slate-500">
            When disabled, we emit <code>&lt;meta name=&quot;robots&quot; content=&quot;noindex,nofollow&quot;/&gt;</code> to keep the site out of Google.
          </p>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-4">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500">
          SEO for {lang.toUpperCase()}
        </div>
        <div className="space-y-1.5">
          <label className="block text-xs font-bold uppercase tracking-widest text-slate-500">Page title</label>
          <input
            type="text"
            value={perLang.title || ""}
            onChange={(e) => onChangeLang(lang, { title: e.target.value })}
            placeholder="MyGlobalTopup — International mobile top-ups"
            maxLength={70}
            className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
            data-testid={`seo-title-${lang}`}
          />
          <div className="text-[10px] text-slate-400">{(perLang.title || "").length} / 70 characters</div>
        </div>
        <div className="space-y-1.5">
          <label className="block text-xs font-bold uppercase tracking-widest text-slate-500">Meta description</label>
          <textarea
            value={perLang.description || ""}
            onChange={(e) => onChangeLang(lang, { description: e.target.value })}
            rows={2}
            maxLength={200}
            placeholder="Reseller portal for international mobile top-ups, gift cards and digital payments."
            className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
            data-testid={`seo-description-${lang}`}
          />
          <div className="text-[10px] text-slate-400">{(perLang.description || "").length} / 200 characters</div>
        </div>
        <div className="space-y-1.5">
          <label className="block text-xs font-bold uppercase tracking-widest text-slate-500">Keywords</label>
          <input
            type="text"
            value={perLang.keywords || ""}
            onChange={(e) => onChangeLang(lang, { keywords: e.target.value })}
            placeholder="mobile topups, reseller, international recharge"
            className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
            data-testid={`seo-keywords-${lang}`}
          />
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <label className="block text-xs font-bold uppercase tracking-widest text-slate-500">Open Graph title</label>
            <input
              type="text"
              value={perLang.og_title || ""}
              onChange={(e) => onChangeLang(lang, { og_title: e.target.value })}
              placeholder="Falls back to the page title if empty"
              className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
              data-testid={`seo-og-title-${lang}`}
            />
          </div>
          <div className="space-y-1.5">
            <label className="block text-xs font-bold uppercase tracking-widest text-slate-500">Open Graph description</label>
            <input
              type="text"
              value={perLang.og_description || ""}
              onChange={(e) => onChangeLang(lang, { og_description: e.target.value })}
              placeholder="Falls back to the meta description"
              className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
              data-testid={`seo-og-description-${lang}`}
            />
          </div>
        </div>

        <div className="text-[11px] text-slate-500">
          OG / share image: whichever landing image slot is chosen in
          <b> Hero background</b> is used automatically (falls back to
          the current hero image when no CMS override is set).
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Social & App Stores editor (Phase 3)
// ---------------------------------------------------------------------------
const SOCIAL_PLATFORMS = [
  { key: "facebook",  label: "Facebook",  placeholder: "https://facebook.com/yourpage" },
  { key: "instagram", label: "Instagram", placeholder: "https://instagram.com/yourpage" },
  { key: "x",         label: "X (Twitter)", placeholder: "https://x.com/yourpage" },
  { key: "youtube",   label: "YouTube",   placeholder: "https://youtube.com/@yourchannel" },
  { key: "tiktok",    label: "TikTok",    placeholder: "https://tiktok.com/@yourpage" },
];

function SocialAppStoresEditor({ social, appStores, onChangeSocial, onChangeStore }) {
  return (
    <div className="space-y-8" data-testid="social-editor">
      <div>
        <h3 className="text-sm font-bold text-slate-800 mb-2">Social media links</h3>
        <p className="text-xs text-slate-500 mb-4">
          Icons appear in the footer only when both a URL is set and the toggle is on.
        </p>
        <div className="space-y-2">
          {SOCIAL_PLATFORMS.map((p) => {
            const item = social?.[p.key] || {};
            return (
              <div key={p.key} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-slate-50" data-testid={`social-row-${p.key}`}>
                <div className="w-24 shrink-0 text-sm font-semibold text-slate-700">{p.label}</div>
                <input
                  type="url"
                  value={item.url || ""}
                  onChange={(e) => onChangeSocial(p.key, { url: e.target.value })}
                  placeholder={p.placeholder}
                  className="flex-1 h-9 rounded-md border border-slate-300 bg-white px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
                  data-testid={`social-url-${p.key}`}
                />
                <label className="inline-flex items-center gap-2 text-xs text-slate-700">
                  <input
                    type="checkbox"
                    checked={!!item.enabled}
                    onChange={(e) => onChangeSocial(p.key, { enabled: e.target.checked })}
                    disabled={!item.url}
                    className="h-4 w-4 accent-[#0F62FE] disabled:opacity-40"
                    data-testid={`social-enabled-${p.key}`}
                  />
                  <span>Show</span>
                </label>
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <h3 className="text-sm font-bold text-slate-800 mb-2">App stores</h3>
        <p className="text-xs text-slate-500 mb-4">
          Toggle badges on/off. If the app is not published yet, keep the &quot;Coming Soon&quot; flag on — the badge renders without linking anywhere.
        </p>
        {[
          { key: "google_play", label: "Google Play badge" },
          { key: "app_store",   label: "Apple App Store badge" },
        ].map((s) => {
          const item = appStores?.[s.key] || {};
          return (
            <div key={s.key} className="mb-3 p-3 rounded-lg border border-slate-200 bg-slate-50" data-testid={`store-row-${s.key}`}>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="w-40 shrink-0 text-sm font-semibold text-slate-700">{s.label}</div>
                <input
                  type="url"
                  value={item.url || ""}
                  onChange={(e) => onChangeStore(s.key, { url: e.target.value })}
                  placeholder="https://play.google.com/store/apps/details?id=…"
                  className="flex-1 min-w-[220px] h-9 rounded-md border border-slate-300 bg-white px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
                  data-testid={`store-url-${s.key}`}
                />
                <label className="inline-flex items-center gap-2 text-xs text-slate-700">
                  <input
                    type="checkbox"
                    checked={item.enabled !== false}
                    onChange={(e) => onChangeStore(s.key, { enabled: e.target.checked })}
                    className="h-4 w-4 accent-[#0F62FE]"
                    data-testid={`store-enabled-${s.key}`}
                  />
                  <span>Show badge</span>
                </label>
                <label className="inline-flex items-center gap-2 text-xs text-slate-700">
                  <input
                    type="checkbox"
                    checked={item.coming_soon !== false}
                    onChange={(e) => onChangeStore(s.key, { coming_soon: e.target.checked })}
                    className="h-4 w-4 accent-amber-500"
                    data-testid={`store-coming-soon-${s.key}`}
                  />
                  <span>Coming Soon</span>
                </label>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Version history browser (Phase 3)
// ---------------------------------------------------------------------------
function HistoryBrowser({ onRestored }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/landing-settings/admin/history?limit=50");
      setRows(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load history");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openDetail = async (id) => {
    setSelectedId(id);
    setDetail(null);
    try {
      const { data } = await api.get(`/landing-settings/admin/history/${id}`);
      setDetail(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load version");
    }
  };

  const restore = async (id) => {
    if (!confirm("Restore this version? It will be re-published immediately.")) return;
    try {
      const { data } = await api.post(`/landing-settings/admin/rollback/${id}`);
      onRestored(data);
      await load();
      toast.success("Version restored and re-published");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to restore");
    }
  };

  if (loading) {
    return <div className="py-6 text-slate-500 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/> Loading history…</div>;
  }
  if (!rows.length) {
    return (
      <div className="py-12 text-center text-slate-500 text-sm border border-dashed border-slate-200 rounded-lg" data-testid="history-empty">
        No published versions yet. Hit <b>Publish</b> at the top to create your first snapshot.
      </div>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-[1fr_1.2fr]" data-testid="history-browser">
      <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-2">
        {rows.map((r) => (
          <button
            type="button"
            key={r.id}
            onClick={() => openDetail(r.id)}
            className={`w-full text-left rounded-lg border p-3 transition-colors ${
              selectedId === r.id ? "border-[#0F62FE] bg-blue-50" : "border-slate-200 bg-white hover:bg-slate-50"
            }`}
            data-testid={`history-row-${r.id}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-800">
                  {new Date(r.at).toLocaleString()}
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  Published by <b>{r.by_username || r.by || "unknown"}</b>
                </div>
                <div className="text-[11px] text-slate-500 mt-1 flex flex-wrap gap-1.5">
                  <Badge>{r.partners_count} operators</Badge>
                  {Object.entries(r.translation_counts || {}).map(([l, n]) => n > 0 && (
                    <Badge key={l}>{l.toUpperCase()} · {n}</Badge>
                  ))}
                  {r.has_images && <Badge>images</Badge>}
                  {(r.seo_langs || []).length > 0 && <Badge>seo</Badge>}
                </div>
              </div>
              <span className="text-[10px] font-mono text-slate-400">{(r.id || "").slice(0, 8)}</span>
            </div>
          </button>
        ))}
      </div>

      <div className="min-w-0">
        {!selectedId ? (
          <div className="rounded-lg border border-dashed border-slate-200 p-8 text-center text-sm text-slate-500">
            Select a version on the left to inspect it and restore.
          </div>
        ) : !detail ? (
          <div className="rounded-lg border border-slate-200 p-6 flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3" data-testid="history-detail">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="text-sm font-bold text-slate-800">Version {detail.id.slice(0, 8)}</div>
                <div className="text-[11px] text-slate-500">
                  {new Date(detail.at).toLocaleString()} · by <b>{detail.by_username || detail.by}</b>
                </div>
              </div>
              <button
                type="button"
                onClick={() => restore(detail.id)}
                className="inline-flex items-center gap-2 h-9 px-3.5 rounded-lg bg-[#0F62FE] text-white text-xs font-semibold hover:bg-[#0050E6]"
                data-testid="history-restore"
              >
                <RotateCcw className="w-3.5 h-3.5" /> Restore this version
              </button>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 text-xs">
              <SnapshotBlock label="Brand" value={detail.snapshot.branding?.brand_name || "(default)"} />
              <SnapshotBlock label="Default language" value={(detail.snapshot.branding?.default_language || "en").toUpperCase()} />
              <SnapshotBlock label="Primary colour" value={detail.snapshot.design?.primary_color || "(default)"} />
              <SnapshotBlock label="Canonical URL" value={detail.snapshot.seo?.canonical_url || "(none)"} />
              <SnapshotBlock label="Indexing" value={detail.snapshot.seo?.allow_indexing !== false ? "Allowed" : "Blocked (noindex)"} />
              <SnapshotBlock label="Operators" value={`${(detail.snapshot.partners || []).length} configured`} />
            </div>
            <div className="text-[11px] text-slate-400 pt-2 border-t border-slate-100">
              Restoring will re-publish this exact snapshot and set your draft to the same state so you can keep editing from there.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Badge({ children }) {
  return (
    <span className="inline-flex items-center rounded-full bg-slate-100 text-slate-600 px-2 py-0.5 text-[10px] font-semibold">
      {children}
    </span>
  );
}

function SnapshotBlock({ label, value }) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1.5">
      <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">{label}</div>
      <div className="text-slate-800 text-xs font-medium mt-0.5 truncate" title={String(value)}>{String(value)}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Integrated preview modal (Phase 3): iframe + device switcher + language.
// ---------------------------------------------------------------------------
const PREVIEW_DEVICES = [
  { id: "desktop", label: "Desktop", w: 1440, h: 900, icon: Monitor },
  { id: "tablet",  label: "Tablet",  w: 768,  h: 1024, icon: Tablet },
  { id: "mobile",  label: "Mobile",  w: 390,  h: 780, icon: Smartphone },
];

export function PreviewModal({ open, onClose, target = "home" }) {
  const [device, setDevice] = useState("desktop");
  const [lang, setLang] = useState("en");
  const [route, setRoute] = useState(target);
  useEffect(() => { setRoute(target); }, [target]);
  if (!open) return null;
  const cfg = PREVIEW_DEVICES.find((d) => d.id === device) || PREVIEW_DEVICES[0];
  const path = route === "login" ? "/login" : "/";
  const src = `${path}?preview_landing=1&preview_lang=${lang}`;
  return (
    <div className="fixed inset-0 z-[80] bg-slate-900/70 backdrop-blur flex items-center justify-center p-4" data-testid="preview-modal">
      <div className="w-full max-w-[95vw] h-[92vh] rounded-2xl bg-white shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 flex-wrap gap-2">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="text-sm font-bold text-slate-800">Preview draft</div>
            <div className="inline-flex items-center rounded-lg border border-slate-200 bg-slate-50 p-1">
              {PREVIEW_DEVICES.map((d) => {
                const Icon = d.icon;
                return (
                  <button
                    key={d.id}
                    type="button"
                    onClick={() => setDevice(d.id)}
                    className={`inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md text-xs font-semibold ${
                      device === d.id ? "bg-white shadow text-[#0F62FE]" : "text-slate-500 hover:text-slate-800"
                    }`}
                    data-testid={`preview-device-${d.id}`}
                  >
                    <Icon className="w-3.5 h-3.5" /> {d.label}
                  </button>
                );
              })}
            </div>
            <div className="inline-flex items-center rounded-lg border border-slate-200 bg-slate-50 p-1">
              {["home", "login"].map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRoute(r)}
                  className={`h-8 px-2.5 rounded-md text-xs font-semibold ${
                    route === r ? "bg-white shadow text-[#0F62FE]" : "text-slate-500 hover:text-slate-800"
                  }`}
                  data-testid={`preview-route-${r}`}
                >
                  {r === "home" ? "Landing" : "Login"}
                </button>
              ))}
            </div>
            <div className="inline-flex items-center rounded-lg border border-slate-200 bg-slate-50 p-1">
              {CMS_LANGUAGES.map((l) => (
                <button
                  key={l.code}
                  type="button"
                  onClick={() => setLang(l.code)}
                  className={`inline-flex items-center gap-1.5 h-8 px-2 rounded-md text-xs font-semibold ${
                    lang === l.code ? "bg-white shadow text-[#0F62FE]" : "text-slate-500 hover:text-slate-800"
                  }`}
                  data-testid={`preview-lang-${l.code}`}
                >
                  <FlagIcon code={l.flag} />
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={`${src}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md border border-slate-300 text-xs font-semibold text-slate-700 hover:bg-slate-50"
              data-testid="preview-open-tab"
            >
              <ExternalLink className="w-3.5 h-3.5" /> Open in new tab
            </a>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800"
              data-testid="preview-close"
            >
              Close
            </button>
          </div>
        </div>
        {/* Iframe wrapper */}
        <div className="flex-1 bg-slate-100 overflow-auto flex items-start justify-center p-6">
          <div
            className="bg-white shadow-xl rounded-lg overflow-hidden border border-slate-200"
            style={{ width: cfg.w, height: cfg.h, maxWidth: "100%" }}
            data-testid="preview-frame-wrap"
          >
            <iframe
              title="Landing preview"
              src={src}
              className="w-full h-full border-0"
              data-testid="preview-frame"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

